/**
 * @author omriha
 * 05/02/2020
 */
import React from 'react';
import {connect} from 'react-redux';
import {push} from 'connected-react-router';
import {useTranslation} from 'react-i18next';
import queryString from 'query-string';
import SMCPage from '../../../components/core/pageComponents/SMCPage';
import ButtonContainer from '../../../components/Simple/ButtonContainer';
import {useEditorDimensions} from '../../../utils/hooks';
import LabelContainer from '../../../components/Page/LabelContainer';
import SMCEditor from '../../../components/Editor';
import {useAdministratorEditor} from './AdministratorEditorHelper';
import SMCButton from '../../../components/Simple/SMCButton';
import RouteLeavingGuard from '../../PopupPages/RouteLeavingGuard';
import {isAdminReadOnlySelector} from '../../../store/selectors/user.selectors';
import {SMCDisabledProvider} from '../../../components/Simple/SMCDisabledContext';
import VerifyButtonsContainer from "../VerifyButtonsContainer";
import ButtonsContainerStyles from "../ButtonsContainerStyles";

/**
 * High level element to fetch, edit and save administrator, all logic implemented inside.
 * @param {string} id Id of administrator to fetch from the server to edit
 */
function AdministratorEditor({
    navigateTo,
    urlParams,
    id,
    isAdminReadOnly
}) {
    const { t } = useTranslation();
    const { editorWidth, editorHeight } = useEditorDimensions();
    const classes = ButtonsContainerStyles();

    let defaultPage = 'general';
    const queryParams = queryString.parse(urlParams);
    if (queryParams && queryParams['page']) {
        defaultPage = queryParams['page'];
    }

    const {
        administratorObject,
        isObjectDirty,
        editorPages,
        helpId,
        origAdministratorObject,
        isPageDataLoaded,
        isInnerPageLoading,
        isPageLoadFailed,
        chosenPageId,
        errorMessagesByPageList,
        onPageChange,
        setAdministratorData,
        saveButtonOnClick,
        deleteButtonOnClick,
        revertButtonOnClick,
        reloadPageData,
        reloadAdministratorStatus,
        minimumPasswordLength,
        maximumPasswordLength
    } = useAdministratorEditor(id, defaultPage);
    
    // Data passed down to the editor pages
    const editorPageData = {
        administratorObject,
        origAdministratorObject,
        setEditorPageId: onPageChange,
        setAdministratorData,
        isPageDataLoaded,
        reloadAdministratorStatus,
        minimumPasswordLength,
        maximumPasswordLength
    }

    return (
        <>
        <RouteLeavingGuard when={isObjectDirty} />
        <SMCPage loading={!isPageDataLoaded} helpId={helpId}>

            <div className="ObjectEditor" style={{ width: editorWidth }}>
                {/* Header */}
                <div className="ObjectEditorHeader">
                    <ButtonContainer>
                        <SMCButton labelClassName={classes.labelColor} icon="delete" variant="ghost" disabled={!isPageDataLoaded} onClick={deleteButtonOnClick}>{t('SMC_GEN_DELETE')}</SMCButton>
                    </ButtonContainer>
                    <LabelContainer label="Name:" value={id} />
                </div>
                <SMCDisabledProvider forceDisable={isAdminReadOnly}>
                    <SMCEditor
                        editorPageData={editorPageData}
                        height={editorHeight}
                        width={editorWidth}
                        errorListByPage={errorMessagesByPageList}
                        chosenPageId={chosenPageId}
                        onPageChange={onPageChange}
                        isPageLoaded={isPageDataLoaded && !isInnerPageLoading}
                        isPageLoadFailed={isPageLoadFailed}
                        reloadData={reloadPageData}
                        pages={editorPages}
                    />
                </SMCDisabledProvider>
            </div>
        </SMCPage>
            <VerifyButtonsContainer
                disabled={!isPageDataLoaded}
                onClickSaveButton={saveButtonOnClick}
                onClickCancelButton={revertButtonOnClick}
            />
        </>
    );
}

const mapStateToProps = (state) => ({
    urlParams: state.router.location.search,
    isAdminReadOnly: isAdminReadOnlySelector(state)
});

const mapDispatchToProps = {
    navigateTo: push
}

export default connect(mapStateToProps, mapDispatchToProps)(AdministratorEditor);